import { Component, ViewEncapsulation } from '@angular/core';
import { LangsRepository } from '../services/langs.repository';
import { AppService } from '../services/app.service';
import { ILang } from '../model/lang.interface';
import { UsersRepository } from '../services/users.repository';
import { User } from '../model/user.model';
import { AssigneesRepositoryBUT } from '../services/assignees.repository.but';
import { AuthService } from '../services/auth.service';
import { environment } from 'src/environments/environment';
import { InAppBrowser } from '@ionic-native/in-app-browser/ngx';

@Component({
    selector: 'users-page',
    templateUrl: 'users.page.html',
    styleUrls: ['users.page.scss', "../app.component.scss"],
    encapsulation: ViewEncapsulation.None
})
export class UsersPage {
    public langsReady = false;
    public usersReady = false;

    public userNewActive: boolean = false;
    public currentUserToEdit: User = null;
    public userEditActive: boolean = false;
    public userReplaceActive: boolean = false;
    public currentReplaceUserId: number = 0;

    constructor(
        private langsRepository: LangsRepository,
        private usersRepository: UsersRepository,
        private appService: AppService,
        private authService: AuthService,
        private assigneesRepositoryBUT: AssigneesRepositoryBUT
    ) {
        this.init();
    }

    public init() {
        this.langsRepository.ready.subscribe(r => { if (r) { this.langsReady = true; } });
        this.usersRepository.prepare().then(() => { this.usersReady = true; });
    }

    get currentLang(): ILang { return this.langsRepository.current; }
    get users(): User[] { return this.usersRepository.users; }
    get user(): User { return this.authService.user; }
    set menuClass(v: string) { this.appService.menuClass = v; }

    /**
     * Mirrors PreparePage logic from CompanyUsersController:
     * Edit is allowed when hasAccessToAdmins OR user is not admin OR editing self.
     * Edit is blocked when !hasAccessToAdmins && user.IsAdmin && user.Id != currentUser.Id
     */
    canEditUser(u: User): boolean {
        const currentUser = this.user;
        if (!currentUser || !u || u.id == null) {
            return false;
        }
        const hasAccessToAdmins = this.hasAccessToOtherAdmins(currentUser);
        if (!hasAccessToAdmins && u.isAdmin && u.id !== currentUser.id) {
            return false;
        }
        return true;
    }

    /**
     * Mirrors CheckIfCurrentUserHasAccessToOtherAdmins from CompanyUsersController:
     * SuperAdmin -> true; CrmId null -> true; CrmId 1 -> true; else false
     */
    private hasAccessToOtherAdmins(currentUser: User): boolean {
        if (currentUser.isSuperAdmin) {
            return true;
        }
        if (currentUser.crmId == null) {
            return true;
        }
        if (currentUser.crmId === 1) {
            return true;
        }
        return false;
    }

    public delete(user: User): void {
        if (confirm(this.currentLang.words["sure"])) {
            this.appService.showNetworkstatus("sending data...");
            this.usersRepository.sendDeleteUser(user).subscribe(res => {
                if (!res.data) {
                    this.appService.showError("API error", this.currentLang.words['delete-admin'], true, res.responseCode);
                } else if (res.responseCode === 200) {
                    this.appService.showNetworkstatus("sent ok");
                    this.usersRepository.delete(user);
                    this.assigneesRepositoryBUT.cacheKey = res.settingsCacheToken;
                } else {
                    this.appService.showError("API error", res.error || res.Error, true, res.responseCode);
                }
            }, err => {
                const message = this.appService.getErrorMessage(err);
                this.appService.showError("HTTP error", `${this.currentLang.words["http_error"]} in users-page delete sendDeleteUser: ${message}`, true);
            });
        }
    }

    public goToEdit(user: User) {
        this.currentUserToEdit = user;
        this.userEditActive = true;
    }

    public openBackendEdit(user: User): void {
        if (!user || !user.id) {
            return;
        }

        const url = new URL("CompanyUsers/Edit", environment.baseFixUrl);
        url.searchParams.set("Id", `${user.id}`);
        url.searchParams.set("companyId", `${user.companyId}`);
        this.openBackendUrl(url.toString());
    }

    public openBackendCreateUser(): void {
        const url = new URL("CompanyUsers/Edit", environment.baseFixUrl);
        const firstUser: any = (this.users && this.users.length > 0) ? this.users[0] : null;
        const companyId = Number(firstUser && firstUser.companyId);
        if (companyId > 0) {
            url.searchParams.set("companyId", `${companyId}`);
        }
        this.openBackendUrl(url.toString());
    }

    private openBackendUrl(url: string): void {
        if (this.appService.isMobile) {
            const browser = new InAppBrowser().create(url, "_system");
            browser.show();
            return;
        }
        window.open(url, "_blank", "noopener,noreferrer");
    }
}
